import { ThemeProvider } from '@oneaudi/unified-web-common';
import React, { useEffect, useRef, useState } from 'react';
import { TextButton, Icon, TextLink } from '@oneaudi/unified-web-components';
import { Logo } from '@oneaudi/aoa-shared-editorial-components';
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import { renderTextWithFootnotesReferences } from '@oneaudi/feature-app-utils';
import uuid from 'react-uuid';
import styled from 'styled-components';
import { getThemeName } from '../../utils/alphaHelper';
import { useWindowSize } from '../../hooks/useWindowSize';
import { withScrollToElement } from '../../componentWrappers/withScrollToElement';
import WLTPComponent from '../MediaDisclaimerAndWLTP/index';
import { useParentSize } from '../../hooks/useParentSize';
import { mediaProperty } from '../../utils/mediaProperty';
import { ResponsiveMedia } from '../ResponsiveMedia/ResponsiveMedia';

import {
  CTAsContainer,
  Container,
  ContentContainer,
  DisclaimerWrapper,
  Heading,
  InnerWrapper,
  LogoContainer,
  MediaContainer,
  NoAnchorContainer,
  StyledAnchor,
  StyledHeadline,
  StyledSubHeadline,
  SubHeading,
} from './styles';

const StyledAssetWrapper = styled.div`
  display: grid;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const StyledTextLink = styled(TextLink)`
  ::before {
    display: none;
  }
  svg {
    margin: 0;
  }
`;

const StyledIcon = styled(Icon)`
  color: ${({ theme }) => (theme === 'light' && '#333333 !important') || '#e5e5e5 !important'};
`;

const ButtonWithScrollToElement = withScrollToElement(TextButton);
const TextButtonWithScrollToElement = withScrollToElement(StyledTextLink);

function Anchor({ linkEnabled, href, newWindow, onClick, children, ariaLabel, linkRef, ...rest }) {
  if (linkEnabled) {
    return (
      <StyledAnchor
        href={href}
        target={newWindow === 'New tab' ? '_blank' : '_self'}
        onClick={onClick}
        aria-label={ariaLabel}
        ref={linkRef}
        {...rest}
      >
        {children}
      </StyledAnchor>
    );
  }

  return <NoAnchorContainer>{children}</NoAnchorContainer>;
}

Anchor.displayName = 'Anchor';

const AnchorWithScroll = withScrollToElement(Anchor);

const stopPropagation = (e) => {
  const { currentTarget } = e;
  if (currentTarget.getAttribute('href')) {
    e.stopPropagation();
  }
};

export function ContentTile({
  CTA,
  disclaimers,
  darkHeadingsEnabled,
  heading,
  horizontalAlignment,
  horizontalAlignmentSmall,
  icon,
  logo,
  media,
  mobileBreakpointSizes,
  size,
  subHeading,
  theme,
  verticalAlignment,
  verticalAlignmentSmall,
  onClickEvent,
  index,
  layoutName,
  wltpKeys,
  isTest,
  ...ueProps
}) {
  const mediaRef = useRef(null);
  const assetContainerLabel = `${media.imageEnabled ? 'Image' : 'Video'} asset`;
  const assetRef = useRef(null);
  const ctaRef = useRef(null);
  const contentRef = useRef(null);
  const linkRef = useRef(null);
  const windowSize = useWindowSize();
  const [isVideoPaused, setIsVideoPaused] = useState(true);
  const { width, height } = useParentSize(mediaRef);

  useEffect(() => {
    if (!mediaRef || !mediaRef.current) return;

    const videos = mediaRef.current.querySelectorAll('video');
    if (!videos.length) return;

    const video = videos[0];
    const updatePausedState = () => setIsVideoPaused(video.paused);

    updatePausedState();
    video.addEventListener('play', updatePausedState);
    video.addEventListener('pause', updatePausedState);

    return () => {
      video.removeEventListener('play', updatePausedState);
      video.removeEventListener('pause', updatePausedState);
    };
  }, [mediaRef.current]);

  const headingType = (heading.variant.slice(0, -1) === 'order' && 'headline') || 'display';
  const headingOrder = heading.variant.at(-1);
  const headingWeight = 'normal';
  const subHeadingType = (subHeading.variant.slice(0, -1) === 'order' && 'headline') || 'display';
  const subHeadingOrder = subHeading.variant.at(-1);
  const subHeadingWeight = 'normal';

  const CTAbutton =
    CTA.variant === 'text' ? TextButtonWithScrollToElement : ButtonWithScrollToElement;

  const CTAprops =
    CTA.variant === 'text'
      ? {
          variant: 'standalone',
          icon: 'forward',
          iconPosition: 'trailing',
        }
      : {
          variant: CTA.variant,
        };

  const ctaLinkType = (linkType) => {
    if (media.assetType === 'image' && CTA.linkType === 'fullTile') {
      return true;
    }
    if (!linkType) return true;

    return false;
  };

  useEffect(() => {
    if (ctaRef && ctaRef.current && CTA.variant === 'text') {
      const cta = ctaRef.current.querySelector('a');
      cta.setAttribute('aria-label', CTA.ariaLabel);
    }
  }, [ctaRef.current]);

  return (
    <AnchorWithScroll
      linkEnabled={ctaLinkType(CTA.linkType)}
      href={CTA.href}
      newWindow={CTA.openIn}
      data-testid="content-tile-image-anchor"
      onClick={onClickEvent}
      ariaLabel={CTA.ariaLabel ? CTA.ariaLabel : CTA.label}
      linkRef={linkRef}
      {...ueProps}
    >
      <ThemeProvider theme={{ name: getThemeName(theme) }}>
        <Container
          data-testid="content-tile-container"
          index={index}
          size={size}
          layoutName={layoutName}
          mobileBreakpointSizes={mobileBreakpointSizes}
          {...ueProps}
          ref={contentRef}
        >
          <InnerWrapper
            data-testid="content-tile-inner-wrapper"
            verticalAlignment={verticalAlignment}
            verticalAlignmentSmall={verticalAlignmentSmall}
            horizontalAlignment={horizontalAlignment}
            horizontalAlignmentSmall={horizontalAlignmentSmall}
          >
            <MediaContainer ref={mediaRef}>
              <UeContainer propertyPath={`tiles.${index}.mediaAsset`} label={assetContainerLabel}>
                <UeReference propertyPath={`tiles.${index}.mediaAsset`} behavior="component">
                  {(ueMediaProps) => (
                    <StyledAssetWrapper ref={assetRef} {...ueMediaProps}>
                      <UeElement
                        property={
                          mediaProperty(media.assetType, windowSize, isVideoPaused).property
                        }
                        type="media"
                        label={mediaProperty(media.assetType, windowSize, isVideoPaused).label}
                      >
                        <ResponsiveMedia
                          {...media}
                          height={height}
                          width={width}
                          theme={theme}
                          isTest={isTest}
                        />
                      </UeElement>
                    </StyledAssetWrapper>
                  )}
                </UeReference>
              </UeContainer>
            </MediaContainer>
            <ContentContainer {...ueProps}>
              <Anchor
                linkEnabled={CTA.linkType === 'textOnly'}
                href={CTA.href}
                newWindow={CTA.openIn}
                data-testid="content-tile-text-anchor"
                onClick={onClickEvent}
                aria-label={CTA.ariaLabel ? CTA.ariaLabel : CTA.label}
              >
                <LogoContainer
                  horizontalAlignment={horizontalAlignment}
                  horizontalAlignmentSmall={horizontalAlignmentSmall}
                >
                  {logo.src && <Logo src={logo.src} alt={logo.alt} heights={logo.heights} />}
                  {!logo.src && icon && <StyledIcon theme={theme} name={icon} size="l" />}
                </LogoContainer>
                {heading.text && (
                  <Heading
                    horizontalAlignment={horizontalAlignment}
                    horizontalAlignmentSmall={horizontalAlignmentSmall}
                  >
                    <StyledHeadline
                      darkHeadingsEnabled={darkHeadingsEnabled}
                      tag={heading.htmlTag}
                      variant={{ order: headingOrder, type: headingType, weight: headingWeight }}
                    >
                      <DisclaimerWrapper onClick={stopPropagation}>
                        <UeElement type="text" property="heading_text" label="Heading">
                          {renderTextWithFootnotesReferences(heading.text)}
                        </UeElement>
                      </DisclaimerWrapper>
                    </StyledHeadline>
                  </Heading>
                )}
                {subHeading.text && !logo.src && !icon && (
                  <SubHeading
                    horizontalAlignment={horizontalAlignment}
                    horizontalAlignmentSmall={horizontalAlignmentSmall}
                  >
                    <StyledSubHeadline
                      darkHeadingsEnabled={darkHeadingsEnabled}
                      tag={subHeading.htmlTag}
                      variant={{
                        order: subHeadingOrder,
                        type: subHeadingType,
                        weight: subHeadingWeight,
                      }}
                    >
                      <DisclaimerWrapper onClick={stopPropagation}>
                        <UeElement type="text" property="subHeading_text" label="Subheading">
                          {renderTextWithFootnotesReferences(subHeading.text)}
                        </UeElement>
                      </DisclaimerWrapper>
                    </StyledSubHeadline>
                  </SubHeading>
                )}
              </Anchor>
              {CTA.text && (
                <CTAsContainer
                  horizontalAlignment={horizontalAlignment}
                  horizontalAlignmentSmall={horizontalAlignmentSmall}
                  ref={ctaRef}
                >
                  <CTAbutton
                    key={uuid()}
                    href={CTA.href}
                    newWindow={CTA.newWindow}
                    data-testid="content-tile-cta"
                    onClick={onClickEvent}
                    size="md"
                    target={CTA.openIn === 'New tab' ? '_blank' : '_self'}
                    {...CTAprops}
                    aria-label={CTA.ariaLabel ? CTA.ariaLabel : CTA.label}
                  >
                    <UeElement type="text" property="CTA_text" label="CTA">
                      {CTA.text}
                    </UeElement>
                  </CTAbutton>
                </CTAsContainer>
              )}
            </ContentContainer>
          </InnerWrapper>
        </Container>
        <WLTPComponent theme={theme} materialDisclaimer={disclaimers} wltpTextValues={wltpKeys} />
      </ThemeProvider>
    </AnchorWithScroll>
  );
}

ContentTile.displayName = 'ContentTile';
